import { POSITION_BADGES } from 'config/portfolio'
import { useMemo } from 'react'
import { V3PositionRowProps } from 'types/portfolio/v3'
import { formatTokenAmountWithUSD, isV3PositionInRange } from 'utils/positionHelpers'
import { Address } from 'viem'

import { PortfolioConnectorLines } from './PortfolioConnectorLines'
import { PortfolioRangeDisplay } from './PortfolioRangeDisplay'
import { PositionRow } from './PositionRow'
import { V3PositionItem } from './V3PositionItem'

export function V3PositionRow({ poolGroup, positionFees, tokenPrices, onCollectSuccess }: V3PositionRowProps) {
  const poolInfo = useMemo(
    () => ({
      token0: poolGroup.token0,
      token1: poolGroup.token1,
      fee: poolGroup.feeTier,
      address: poolGroup.poolAddress,
    }),
    [poolGroup.token0, poolGroup.token1, poolGroup.feeTier, poolGroup.poolAddress]
  )

  const firstPosition = poolGroup.positions[0]
  const showMainRowRange = poolGroup.positions.length === 1

  const { tokenBalancesDisplay, unclaimedFeesDisplay } = useMemo(() => {
    const aggregatedBalances = poolGroup.positions.reduce(
      (acc, position) => {
        const token0Amount = parseFloat(position.sdkPosition.amount0.toExact())
        const token1Amount = parseFloat(position.sdkPosition.amount1.toExact())

        return {
          token0: acc.token0 + token0Amount,
          token1: acc.token1 + token1Amount,
        }
      },
      { token0: 0, token1: 0 }
    )

    const token0Price = tokenPrices?.[firstPosition.enrichedToken0.id.toLowerCase() as Address] || 0
    const token1Price = tokenPrices?.[firstPosition.enrichedToken1.id.toLowerCase() as Address] || 0

    const token0USD = aggregatedBalances.token0 * token0Price
    const token1USD = aggregatedBalances.token1 * token1Price

    const balancesDisplay = {
      token0: `${aggregatedBalances.token0.toFixed(6)} ${firstPosition.enrichedToken0.symbol} ($${token0USD.toFixed(
        2
      )})`,
      token1: `${aggregatedBalances.token1.toFixed(6)} ${firstPosition.enrichedToken1.symbol} ($${token1USD.toFixed(
        2
      )})`,
    }

    const aggregatedFees = poolGroup.positions.reduce(
      (acc, position) => {
        const fees = positionFees?.[position.nftId]
        const fee0Amount = fees?.amount0 || position.uncollectedFeesToken0
        const fee1Amount = fees?.amount1 || position.uncollectedFeesToken1

        const fee0Decimal = parseFloat(fee0Amount) / Math.pow(10, position.sdkPosition.pool.token0.decimals)
        const fee1Decimal = parseFloat(fee1Amount) / Math.pow(10, position.sdkPosition.pool.token1.decimals)

        return {
          fee0: acc.fee0 + fee0Decimal,
          fee1: acc.fee1 + fee1Decimal,
          fee0Raw: (BigInt(acc.fee0Raw) + BigInt(fee0Amount || '0')).toString(),
          fee1Raw: (BigInt(acc.fee1Raw) + BigInt(fee1Amount || '0')).toString(),
        }
      },
      { fee0: 0, fee1: 0, fee0Raw: '0', fee1Raw: '0' }
    )

    const aggregatedFee0USD = aggregatedFees.fee0 * token0Price
    const aggregatedFee1USD = aggregatedFees.fee1 * token1Price

    const feesDisplay =
      aggregatedFees.fee0 > 0 || aggregatedFees.fee1 > 0
        ? {
            token0: formatTokenAmountWithUSD(
              aggregatedFees.fee0Raw,
              firstPosition.enrichedToken0.symbol,
              parseInt(firstPosition.enrichedToken0.decimals),
              aggregatedFee0USD
            ),
            token1: formatTokenAmountWithUSD(
              aggregatedFees.fee1Raw,
              firstPosition.enrichedToken1.symbol,
              parseInt(firstPosition.enrichedToken1.decimals),
              aggregatedFee1USD
            ),
          }
        : undefined

    return {
      tokenBalancesDisplay: balancesDisplay,
      unclaimedFeesDisplay: feesDisplay,
    }
  }, [poolGroup.positions, positionFees, tokenPrices, firstPosition])

  const mainRowBadges = useMemo(() => {
    const v3Badge = (
      <span
        className="px-1.5 sm:px-1.5 py-0.5 sm:py-1 rounded-xs text-[11px] font-medium whitespace-nowrap"
        style={{ backgroundColor: POSITION_BADGES.v3.bgColor, color: POSITION_BADGES.v3.textColor }}
      >
        {POSITION_BADGES.v3.label}
      </span>
    )

    const rangeStatuses = poolGroup.positions.map((pos) => isV3PositionInRange(pos))

    const allInRange = rangeStatuses.every((status) => status === true)
    const allOutOfRange = rangeStatuses.every((status) => status === false)

    let rangeBadge = null
    if (allInRange || allOutOfRange) {
      const badge = allInRange ? POSITION_BADGES.inRange : POSITION_BADGES.outOfRange
      rangeBadge = (
        <span
          className="flex gap-1 items-center px-1.5 sm:px-1.5 py-0.5 sm:py-1 rounded-xs text-[11px] font-medium whitespace-nowrap"
          style={{ backgroundColor: badge.bgColor, color: badge.textColor }}
        >
          <div className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: badge.dotColor }} />
          {badge.label}
        </span>
      )
    }

    return (
      <div className="flex items-center gap-1 sm:gap-1.5 shrink-0 whitespace-nowrap">
        {v3Badge}
        {rangeBadge}
      </div>
    )
  }, [poolGroup.positions])

  return (
    <PositionRow
      pool={poolInfo}
      totalValue={poolGroup.totalValue}
      subRowCount={poolGroup.positions.length}
      rangeDisplay={
        showMainRowRange ? (
          <PortfolioRangeDisplay
            token0Address={firstPosition.enrichedToken0.id}
            token1Address={firstPosition.enrichedToken1.id}
            lowerTick={firstPosition.sdkPosition.tickLower}
            upperTick={firstPosition.sdkPosition.tickUpper}
            currentTick={firstPosition.sdkPosition.pool.tickCurrent}
            feeTier={poolGroup.feeTier}
            poolType="V3"
          />
        ) : undefined
      }
      tokenBalancesDisplay={tokenBalancesDisplay}
      unclaimedFeesDisplay={unclaimedFeesDisplay}
      badges={mainRowBadges}
    >
      {poolGroup.positions.map((position, index) => {
        const isFirstItem = index === 0
        const isLastItem = index === poolGroup.positions.length - 1

        return (
          <div key={position.nftId} className="relative">
            <PortfolioConnectorLines isFirst={isFirstItem} isLast={isLastItem} mobileTop="20px" />

            <V3PositionItem
              position={position}
              fees={positionFees?.[position.nftId]}
              tokenPrices={tokenPrices}
              onCollectSuccess={onCollectSuccess}
              variant="grouped"
              showRange={!showMainRowRange}
              showMobileRange
              showRangePlaceholder={!showMainRowRange}
            />
          </div>
        )
      })}
    </PositionRow>
  )
}
